import React, { FC, Suspense, useEffect, useState } from "react";
import styles from "./Home.module.scss";
import { GetServerSideProps } from "next";
import path from "path";
import fs from "fs";
import { NextSeo } from "next-seo";
import { Skeleton } from "@mui/material";
import { useAuth } from "@/context/AuthContext";
import { destroyCookie, parseCookies, setCookie } from "nookies";
import NewSearchForm from "@/components/forms/SearchFormV2/SearchFormV2";
import OurFeatures from "@/components/cards/OurFeatures/OurFeatures";
import TopVisitedDestinations from "@/components/cards/TopVisitedDestinations/TopVisitedDestinations";
import VacationsCard from "@/components/cards/VacationsCard/VacationsCard";
import ExperiencePromoSection from "@/components/ExperiencePromoSection/ExperiencePromoSection";

import TopHotels from "@/components/cards/TopHotels/TopHotels";
import DealHubSection from "@/components/cards/DealHubSection/DealHubSection";
import FestivalEscapeFrame from "@/components/FestivalEscapeFrame/FestivalEscapeFrame";
import SearchFormMobile from "@/components/forms/SearchFormMobile/SearchFormMobile";
import { useTranslations } from "next-intl";
import {
  FlightIcon,
  HotelIcon,
  SolidFlightIcon,
  SolidHotelIcon,
} from "@/components/icons";
import FlightsFormMobile from "@/components/forms/FlightsFormMobile/FlightsFormMobile";
import Head from "next/head";
import { useRouter } from "next/router";

interface HomeProps {
  translations: any;
}

const Home: FC<HomeProps> = ({ translations }) => {
  const t = useTranslations("");
  const { locale } = useRouter();

  const { isAuthenticated } = useAuth();
  useEffect(() => {
    const ensureGuestToken = async () => {
      try {
        if (isAuthenticated) {
          destroyCookie(null, "next-auth.guest-token");
          return;
        } else {
          const data= await fetch("/api/guest-token", { method: "POST" });
        }
      } catch (error) {
        console.error("Token verification failed:", error);
      }
    };

    ensureGuestToken();
  }, [isAuthenticated]);
  
  const travelOptions = [
    {
      id: "hotel",
      label: t("searchForm.hotel"),
      icon: <HotelIcon className="text-primary" />,
      activeIcon: <SolidHotelIcon className="text-primary" />,
    },
    // {
    //   id: "flight",
    //   label: t("searchForm.flight"),
    //   icon: <FlightIcon className="text-primary " />,
    //   activeIcon: <SolidFlightIcon className="text-primary" />,
    // },
  ];

  const { asPath } = useRouter();
  const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL}${
    asPath.split("?")[0]
  }`;

  const [selectedTab, setSelectedTab] = useState("hotel");
  return (
    <>
      <Head>
        <title>
          {locale === "en"
            ? "Almuayed: Hotels and Flights"
            : "Almuayed | حجز طيران وفنادق"}
        </title>
        <meta
          name="description"
          content={
            locale === "en"
              ? "Book flights and hotels easily with Almuayed across Saudi Arabia and the Gulf..."
              : "احجز رحلات الطيران والفنادق بسهولة مع Almuayed..."
          }
        />
        <meta property="og:title" content="Almuayed: Hotels and Flights" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Almuayed" />
        <meta
          property="og:description"
          content={
            locale === "en"
              ? "Book flights and hotels easily with Almuayed across Saudi Arabia and the Gulf..."
              : "احجز رحلات الطيران والفنادق بسهولة مع Almuayed..."
          }
        />

        <meta property="og:url" content={process.env.NEXT_PUBLIC_SITE_URL} />

        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_SITE_URL}/logo.png`}
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <NextSeo
        title="Almuayed: Hotels and Flights"
        description="Almuayed | Flights & Hotels in Saudi Arabia & the Gulf Book flights and hotels easily with Almuayed across Saudi Arabia and the Gulf. Find the best prices, secure payments, and 24/7 support for a seamless travel experience"
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_SITE_URL}`,
          title: "Almuayed: Hotels and Flights",
          description:
            "Almuayed | Flights & Hotels in Saudi Arabia & the Gulf Book flights and hotels easily with Almuayed across Saudi Arabia and the Gulf. Find the best prices, secure payments, and 24/7 support for a seamless travel experience",
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_SITE_URL}/logo.png`,
              width: 800,
              height: 600,
              alt: "Almuayed: Hotels and Flights",
            },
          ],
          site_name: "Almuayed",
        }}
      />
      <h1 className="sr-only">Almuayed: Hotels and Flights</h1>
      <h3 className="sr-only">
        Book flights and hotels easily with Almuayed across Saudi Arabia and the
        Gulf...
      </h3>
      <p className="sr-only">
        Almuayed | Flights & Hotels in Saudi Arabia & the Gulf Book flights and hotels
        easily with Almuayed across Saudi Arabia and the Gulf. Find the best prices,
        secure payments, and 24/7 support for a seamless travel experience
      </p>
      <div className={`${styles.Home} min-h-screen `} data-testid="Home">
        <div className="c-lg:flex hidden">
          <NewSearchForm />
        </div>

        <div className=" flex-col w-full h-fit mt-5  items-center    gap-4 px-4 py-0 relative mb-16 max-c-lg:flex hidden">
          {" "}
          <div className="relative self-stretch w-full px-8   justify-center flex gap-[10px] h-[87px] ">
            {" "}
            {travelOptions.map((tab) => (
              <button
                key={tab.id}
                className={`flex flex-col w-[137px] h-[87px] items-center justify-center !gap-3 p-4
               ${
                 selectedTab === tab.id
                   ? "bg-[#dfe9ff] border border-solid border-[#1e2f56]"
                   : "bg-white !shadow-[8px_8px_35px_0_rgba(15,44,89,0.07)]"
               } rounded-[20px] shadow-drop-shadow-200 h-full`}
                onClick={() => setSelectedTab(tab.id)}
              >
                {" "}
                <div className="relative flex flex-col justify-center items-center gap-3 h-full  ">
                  {" "}
                  <div className="  ">
                    {" "}
                    {selectedTab === tab.id ? tab.activeIcon : tab.icon}
                  </div>{" "}
                  <div className="  font-medium  text-base text-center tracking-[0] leading-[normal]">
                    {" "}
                    {tab.label}{" "}
                  </div>{" "}
                </div>{" "}
              </button>
            ))}{" "}
          </div>{" "}
          {/* {selectedTab === "hotel" ? ( */}
            <SearchFormMobile />
          {/* ) : (
            <FlightsFormMobile />
          )} */}
        </div>

        <Suspense
          fallback={
            <Skeleton variant="rectangular" className="w-full" height={500} />
          }
        >
          <div className="flex flex-col c-lg:gap-20 gap-16 text-primary">
            <FestivalEscapeFrame
              carouselItems={translations.carouselItems}
              promoCards={translations.promoCards}
              heroSection={translations.heroSection}
            />
            <DealHubSection data={translations.dealHubData} />
            <TopHotels topHotelsData={translations.topHotelsData} />
            <ExperiencePromoSection
              ExperiencePromoSection={translations.ExperiencePromoSection}
              emailSubscriptionData={translations.emailSubscriptionData}
            />
            <TopVisitedDestinations
              data={translations.TopVisitedDestinations}
            />
            <VacationsCard data={translations.vacations} />
            <OurFeatures data={translations.Features} />
          </div>
        </Suspense>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const cookies = parseCookies(context);
    const { locale, res } = context;
    res.setHeader(
      "Cache-Control",
      "private, max-age=0, must-revalidate, stale-while-revalidate=60"
    );

    const commonFilePath = path.join(
      process.cwd(),
      "src",
      "locales",
      locale ?? "en",
      "common.json"
    );
    const commonData = fs.readFileSync(commonFilePath, "utf-8");
    const translations = JSON.parse(commonData);

    return {
      props: {
        translations,
        locale,
      },
    };
  } catch (error) {
    console.error("Error loading translations:", error);
    return {
      notFound: true,
    };
  }
};

export default Home;
